import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './MiAgenda.css';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const HORAS = ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'];

const MiAgenda = () => {
    const { user } = useAuth();
    const [fechaActual, setFechaActual] = useState(new Date());
    const [turnos, setTurnos] = useState([]);
    const [bloqueos, setBloqueos] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Día seleccionado para el panel lateral
    const [diaSeleccionado, setDiaSeleccionado] = useState(null);
    const [guardando, setGuardando] = useState(false);

    // ============================================
    // CARGAR DATOS DEL MES
    // ============================================
    const cargarDatos = async () => {
        try {
            setLoading(true);
            const mes = fechaActual.getMonth() + 1;
            const anio = fechaActual.getFullYear();
            const res = await api.get(`/agenda?mes=${mes}&anio=${anio}`);
            setTurnos(res.data.turnos);
            setBloqueos(res.data.bloqueos);
        } catch (error) {
            console.error('Error al cargar agenda:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarDatos();
    }, [fechaActual]);

    // ============================================
    // NAVEGACIÓN DEL MES
    // ============================================
    const cambiarMes = (delta) => {
        const nuevaFecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + delta, 1);
        setFechaActual(nuevaFecha);
        setDiaSeleccionado(null);
    };

    // ============================================
    // UTILIDADES DEL CALENDARIO
    // ============================================
    const getDiasDelMes = () => {
        const anio = fechaActual.getFullYear();
        const mes = fechaActual.getMonth();
        const primerDia = new Date(anio, mes, 1);
        const ultimoDia = new Date(anio, mes + 1, 0);
        
        const diasVaciosInicio = primerDia.getDay();
        const totalDias = ultimoDia.getDate();
        
        const dias = [];
        for (let i = 0; i < diasVaciosInicio; i++) dias.push(null);
        for (let i = 1; i <= totalDias; i++) dias.push(new Date(anio, mes, i));
        
        return dias;
    };

    const formatearFechaStr = (date) => {
        if (!date) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // ============================================
    // LÓGICA DEL DÍA SELECCIONADO
    // ============================================
    const handleDiaClick = (dia) => {
        if (!dia) return;
        const fechaStr = formatearFechaStr(dia);
        setDiaSeleccionado({ date: dia, fechaStr });
    };

    const turnosDelDia = diaSeleccionado 
        ? turnos.filter(t => new Date(t.fecha).toISOString().slice(0, 10) === diaSeleccionado.fechaStr)
        : [];
        
    const bloqueosDelDia = diaSeleccionado
        ? bloqueos.filter(b => b.fecha === diaSeleccionado.fechaStr)
        : [];

    const diaCompletoBloqueado = bloqueosDelDia.find(b => b.todoElDia);

    // ============================================
    // ACCIONES DE BLOQUEO
    // ============================================
    const toggleBloqueoDiaCompleto = async () => {
        if (!diaSeleccionado) return;
        setGuardando(true);
        try {
            if (diaCompletoBloqueado) {
                await api.delete(`/agenda/bloquear/${diaCompletoBloqueado._id}`);
            } else {
                await api.post('/agenda/bloquear', {
                    fecha: diaSeleccionado.fechaStr,
                    todoElDia: true,
                    motivo: 'Vacaciones / Día libre'
                });
            }
            await cargarDatos();
        } catch (error) {
            alert(error.response?.data?.message || 'Error al cambiar disponibilidad');
        } finally {
            setGuardando(false);
        }
    };

    const toggleBloqueoHora = async (hora) => {
        if (!diaSeleccionado) return;
        setGuardando(true);
        try {
            const bloqueoExistente = bloqueosDelDia.find(b => b.hora === hora);
            if (bloqueoExistente) {
                await api.delete(`/agenda/bloquear/${bloqueoExistente._id}`);
            } else {
                await api.post('/agenda/bloquear', {
                    fecha: diaSeleccionado.fechaStr,
                    todoElDia: false,
                    hora,
                    motivo: 'No disponible'
                });
            }
            await cargarDatos();
        } catch (error) {
            alert(error.response?.data?.message || 'Error al cambiar disponibilidad');
        } finally {
            setGuardando(false);
        }
    };

    // ============================================
    // RENDER
    // ============================================
    const dias = getDiasDelMes();
    const hoyStr = formatearFechaStr(new Date());

    return (
        <div className="agenda-wrapper">
            <div className="agenda-header">
                <h2>🗓️ Mi Agenda</h2>
                <p>Gestioná tus turnos y bloqueá tu disponibilidad</p>
            </div>

            <div className="agenda-content">
                {/* Calendario */}
                <div className="calendario-container">
                    <div className="calendario-header">
                        <button className="btn-nav" onClick={() => cambiarMes(-1)}>◀</button>
                        <h3>{MESES[fechaActual.getMonth()]} {fechaActual.getFullYear()}</h3>
                        <button className="btn-nav" onClick={() => cambiarMes(1)}>▶</button>
                    </div>

                    <div className="calendario-grid">
                        {DIAS_SEMANA.map(d => (
                            <div key={d} className="calendario-dia-semana">{d}</div>
                        ))}
                        
                        {dias.map((dia, index) => {
                            if (!dia) return <div key={`empty-${index}`} className="calendario-celda vacia"></div>;
                            
                            const fechaStr = formatearFechaStr(dia);
                            const esHoy = fechaStr === hoyStr;
                            const esSeleccionado = diaSeleccionado?.fechaStr === fechaStr;
                            
                            // Contar turnos del día
                            const cantTurnos = turnos.filter(t => new Date(t.fecha).toISOString().slice(0, 10) === fechaStr).length;
                            
                            // Evaluar bloqueos de este día
                            const bloqueosDelDia = bloqueos.filter(b => b.fecha === fechaStr);
                            const bloqueadoTodoElDia = bloqueosDelDia.some(b => b.todoElDia);
                            const tieneBloqueosParciales = !bloqueadoTodoElDia && bloqueosDelDia.length > 0;

                            return (
                                <div 
                                    key={fechaStr} 
                                    className={`
                                        calendario-celda 
                                        ${esHoy ? 'hoy' : ''} 
                                        ${esSeleccionado ? 'seleccionado' : ''} 
                                        ${bloqueadoTodoElDia ? 'bloqueado-full' : ''} 
                                        ${tieneBloqueosParciales ? 'bloqueado-parcial' : ''}
                                    `}
                                    onClick={() => handleDiaClick(dia)}
                                >
                                    <span className="dia-numero">{dia.getDate()}</span>
                                    
                                    <div className="dia-badges">
                                        {bloqueadoTodoElDia ? (
                                            <span className="dia-badge icono-bloqueo" title="Día bloqueado / Vacaciones">🚫</span>
                                        ) : tieneBloqueosParciales ? (
                                            <span className="dia-badge icono-parcial" title="Tiene horarios bloqueados">⏳</span>
                                        ) : cantTurnos > 0 ? (
                                            <span className="dia-badge turnos">{cantTurnos} turno{cantTurnos > 1 ? 's' : ''}</span>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Panel lateral */}
                {diaSeleccionado ? (
                    <div className="panel-lateral">
                        <div className="panel-header">
                            <h4>
                                {diaSeleccionado.date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </h4>
                            <button className="btn-close" onClick={() => setDiaSeleccionado(null)}>×</button>
                        </div>

                        <div className="panel-body">
                            <div className="bloqueo-dia-completo">
                                <label className="switch-label">
                                    <input 
                                        type="checkbox" 
                                        checked={!!diaCompletoBloqueado}
                                        onChange={toggleBloqueoDiaCompleto}
                                        disabled={guardando}
                                    />
                                    <span>Bloquear día completo (Vacaciones / Día libre)</span>
                                </label>
                            </div>

                            {!diaCompletoBloqueado && (
                                <>
                                    <h5 className="panel-subtitle">Turnos Agendados</h5>
                                    {turnosDelDia.length === 0 ? (
                                        <p className="text-muted">No hay turnos agendados.</p>
                                    ) : (
                                        <ul className="turnos-lista-panel">
                                            {turnosDelDia.map(t => (
                                                <li key={t._id} className="turno-item-panel">
                                                    <span className="hora">{new Date(t.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs</span>
                                                    <span className="paciente">{t.paciente.nombre} {t.paciente.apellido}</span>
                                                    <span className={`estado ${t.estado}`}>{t.estado}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <h5 className="panel-subtitle" style={{ marginTop: '1.5rem' }}>Disponibilidad por hora</h5>
                                    <div className="horas-grid-panel">
                                        {HORAS.map(hora => {
                                            const turnoOcupado = turnosDelDia.some(t => new Date(t.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) === hora);
                                            const bloqueoHora = bloqueosDelDia.find(b => b.hora === hora);
                                            
                                            return (
                                                <div key={hora} className={`hora-slot ${turnoOcupado ? 'ocupado' : ''} ${bloqueoHora ? 'bloqueado' : ''}`}>
                                                    <span className="hora-label">{hora}</span>
                                                    {turnoOcupado ? (
                                                        <span className="hora-estado">Turno</span>
                                                    ) : (
                                                        <label className="switch-mini">
                                                            <input 
                                                                type="checkbox"
                                                                checked={!!bloqueoHora}
                                                                onChange={() => toggleBloqueoHora(hora)}
                                                                disabled={guardando}
                                                            />
                                                            <span className="slider"></span>
                                                        </label>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="panel-lateral empty">
                        <p>Seleccioná un día en el calendario para ver los turnos y gestionar tu disponibilidad.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MiAgenda;